import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Code,
  Github,
  Twitter,
  Mail,
  Linkedin,
  Heart,
  Send,
  Globe,
  Zap,
  Shield,
  ChevronUp,
  Check,
  Sparkles
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/hooks/use-toast";
import { useGenericRequestMutation } from "@/store/features/custom/customAPI";


const footerSections = [
  {
    title: "Product",
    links: [
      { name: "Discover Events", href: "/discover" },
      { name: "Generate Event", href: "/generate", badge: "AI" },
      { name: "Pricing", href: "/pricing" },
      { name: "Templates", href: "/templates" }
    ]
  },
  {
    title: "Company",
    links: [
      { name: "About", href: "/about" },
      { name: "Portfolio", href: "/portfolio" },
      { name: "Contact", href: "/contact" }
    ]
  },
  {
    title: "Resources",
    links: [
      { name: "Email Marketing", href: "/email-marketing" },
      { name: "AI Assistant", href: "/ai-assistant", badge: "New" },
      { name: "Integrations", href: "/integrations" }
    ]
  },
  {
    title: "Legal",
    links: [
      { name: "Privacy Policy", href: "/privacy" },
      { name: "Terms of Service", href: "/terms" }
    ]
  }
];


const socialLinks = [
  { name: "GitHub", href: "https://github.com/yourusername", icon: Github, color: "#181717" },
  { name: "Twitter", href: "https://twitter.com/yourusername", icon: Twitter, color: "#1DA1F2" },
  { name: "LinkedIn", href: "https://linkedin.com/company/yourcompany", icon: Linkedin, color: "#0A66C2" },
  { name: "Email", href: "mailto:hello@AboutBlank.app", icon: Mail, color: "rgb(var(--theme-primary))" }
];


const bottomStats = [
  { icon: Globe, text: "Available worldwide" },
  { icon: Zap, text: "99.9% uptime" },
  { icon: Shield, text: "SOC 2 compliant" }
];


export function LandingFooter() {
  const [email, setEmail] = useState("");
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [hoveredLink, setHoveredLink] = useState<string | null>(null);
  const [hoveredSocial, setHoveredSocial] = useState<string | null>(null);

  // RTK Query mutation hook
  const [genericRequest] = useGenericRequestMutation();


  // Newsletter subscription using RTK Query
  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    const emailValue = email.trim();
    if (!emailValue) return;

    setIsSubscribing(true);

    try {
      const res: any = await genericRequest({
        url: '/news-letters',
        method: 'POST',
        data: { data: { email: emailValue } }
      }).unwrap();

      setIsSubscribed(true);
      toast({
        title: "✅ Successfully subscribed!",
        description: "You're on the list! Thank you for subscribing."
      });
      setEmail("");
      setTimeout(() => setIsSubscribed(false), 3000);
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "❌ Subscription failed",
        description:
          error?.data?.error?.message ||
          error?.message ||
          "Your email address has been already subscribed or an error occurred."
      });
    } finally {
      setIsSubscribing(false);
    }
  };


  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };


  return (
    <footer className="relative bg-background border-t overflow-hidden"
      style={{ borderColor: `rgba(var(--border), 0.5)` }}
    >
      {/* Animated Background Pattern */}
      <div className="absolute inset-0 opacity-30 dark:opacity-20 pointer-events-none">
        <div
          className="absolute inset-0 animate-pulse"
          style={{
            backgroundImage: `
              linear-gradient(90deg, rgba(var(--theme-primary), 0.02) 1px, transparent 1px),
              linear-gradient(rgba(var(--theme-primary), 0.02) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px'
          }}
        />
      </div>
      {/* Gradient Orbs */}
      <div className="absolute top-0 right-1/4 w-96 h-96 rounded-full opacity-10 blur-3xl animate-pulse"
        style={{
          background: `radial-gradient(circle, rgb(var(--theme-primary)) 0%, transparent 70%)`
        }}
      />
      <div className="absolute bottom-0 left-1/4 w-96 h-96 rounded-full opacity-10 blur-3xl animate-pulse delay-1000"
        style={{
          background: `radial-gradient(circle, rgb(var(--theme-secondary)) 0%, transparent 70%)`
        }}
      />

      <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8 mb-12">

          {/* Brand Section */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-block group">
              <div className="flex items-center space-x-2 mb-4">
                <div className="relative">
                  <div
                    className="absolute inset-0 rounded-lg opacity-20 blur-sm"
                    style={{
                      background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`
                    }}
                  />
                  <div
                    className="relative flex h-10 w-10 items-center justify-center rounded-lg group-hover:scale-110 group-hover:rotate-6 transition duration-300"
                    style={{
                      background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`
                    }}
                  >
                    <Code className="h-5 w-5 text-white" />
                  </div>
                </div>
                <span
                  className="text-xl font-bold"
                  style={{
                    background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text"
                  }}
                >
                  AboutBlank AI
                </span>
              </div>
            </Link>
            <p className="text-muted-foreground mb-6 max-w-sm leading-relaxed">
              Transform your event ideas into stunning, production-ready platforms with the power of AI. Build faster, launch sooner.
            </p>
            {/* Newsletter Signup */}
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Send className="h-4 w-4" style={{ color: `rgb(var(--theme-primary))` }} />
                Stay Updated
              </h4>
              <form onSubmit={handleSubscribe} className="flex max-w-md relative">
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  disabled={isSubscribing || isSubscribed}
                  className="flex-1 px-3 py-2 text-sm border rounded-l-lg bg-background text-foreground focus:outline-none focus:ring-2 transition-all disabled:opacity-50"
                  style={{ borderColor: `rgba(var(--border), 0.5)` }}
                  required
                />
                <Button
                  type="submit"
                  size="sm"
                  disabled={isSubscribing || isSubscribed}
                  className="rounded-l-none rounded-r-lg text-white shadow-lg hover:shadow-xl transition-all duration-300 group relative overflow-hidden"
                  style={{
                    background: isSubscribed
                      ? "rgb(34, 197, 94)"
                      : `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`
                  }}
                >
                  {isSubscribing ? (
                    <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : isSubscribed ? (
                    <Check className="h-4 w-4" />
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-1" />
                      Subscribe
                    </>
                  )}
                </Button>
              </form>
              <p className="text-xs text-muted-foreground mt-2">
                Join 10,000+ subscribers. Unsubscribe anytime.
              </p>
            </div>
            {/* Social links */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                Follow Us
              </p>
              <div className="flex space-x-3">
                {socialLinks.map((social) => {
                  const IconComponent = social.icon;
                  const isHovered = hoveredSocial === social.name;
                  return (
                    <a
                      key={social.name}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group relative flex h-10 w-10 items-center justify-center rounded-lg transition-all duration-300 hover:scale-110 hover:-translate-y-1"
                      style={{
                        background: isHovered
                          ? `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`
                          : `rgba(var(--theme-primary), 0.1)`
                      }}
                      aria-label={social.name}
                      onMouseEnter={() => setHoveredSocial(social.name)}
                      onMouseLeave={() => setHoveredSocial(null)}
                    >
                      <IconComponent
                        className="h-5 w-5 transition-colors duration-300"
                        style={{ color: isHovered ? "white" : social.color }}
                      />
                      {/* Tooltip */}
                      <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-foreground text-background text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                        {social.name}
                      </span>
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
          {/* Footer sections (Product/Company/Resources/Legal) */}
          {footerSections.map((section) => (
            <div key={section.title}>
              <h3 className="text-sm font-semibold text-foreground uppercase tracking-wider mb-4 flex items-center gap-2">{section.title}</h3>
              <ul className="space-y-3">
                {section.links.map(link => (
                  <li key={link.name} className="flex items-center gap-2">
                    <Link
                      to={link.href}
                      className="group inline-flex items-center text-muted-foreground hover:text-foreground transition-colors duration-200 flex-1"
                      onMouseEnter={() => setHoveredLink(link.name)}
                      onMouseLeave={() => setHoveredLink(null)}
                    >
                      {link.name}
                      <span
                        className={`ml-1 transition-all duration-300 ${hoveredLink === link.name ? 'translate-x-1 opacity-100' : 'translate-x-0 opacity-0'}`}
                        style={{ color: `rgb(var(--theme-primary))` }}>→</span>
                    </Link>
                    {link.badge && (
                      <Badge variant="secondary" className="text-xs px-1.5 py-0.5 bg-gradient-primary text-white">
                        {link.badge}
                      </Badge>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        {/* Bottom section */}
        <div className="pt-8 border-t flex flex-col md:flex-row justify-between items-center gap-4"
          style={{ borderColor: `rgba(var(--border), 0.5)` }}
        >
          <div className="flex flex-col md:flex-row items-center gap-2 text-sm text-muted-foreground">
            <span className="flex items-center gap-2">
              © {new Date().getFullYear()} AboutBlank AI. All rights reserved.
            </span>
            <span className="hidden md:inline">•</span>
            <span className="flex items-center gap-1.5">
              Made with <Heart className="h-4 w-4 text-red-500 fill-red-500 animate-pulse" /> by Amandy Labs Private Limited
            </span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
            {bottomStats.map((item, idx) => {
              const Icon = item.icon;
              return (
                <div key={idx} className="group flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors cursor-pointer">
                  <Icon className="h-4 w-4 transition-transform duration-300 group-hover:scale-110" style={{ color: `rgb(var(--theme-primary))` }} />
                  <span>{item.text}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {/* Scroll to Top Button */}
      <button
        onClick={scrollToTop}
        className="fixed bottom-8 right-8 z-50 p-3 rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-110 group"
        style={{
          background: `linear-gradient(135deg, rgb(var(--theme-primary)), rgb(var(--theme-secondary)))`
        }}
        aria-label="Scroll to top"
      >
        <ChevronUp className="h-5 w-5 text-white group-hover:animate-bounce" />
      </button>
    </footer>
  );
}
